import { useCallback, useEffect, useRef } from 'react';

interface UseIntersectionRefOptions {
  /** Observe only while true; flipping it re-arms the observer (and re-fires if still visible). */
  enabled?: boolean;
  /** Grow/shrink the viewport the sentinel is tested against. */
  rootMargin?: string;
}

/**
 * A ref callback for an "infinite scroll" sentinel: calls `onIntersect` whenever
 * the element is (or scrolls into) view while `enabled`. Because re-arming
 * re-reports the current visibility, a list that is still shorter than its
 * container after a page loaded keeps loading until it fills or runs out —
 * pass `enabled: hasMore && !isFetching`.
 *
 * No-ops where `IntersectionObserver` is missing (jsdom, very old browsers).
 */
export function useIntersectionRef<T extends Element>(
  onIntersect: () => void,
  { enabled = true, rootMargin = '0px' }: UseIntersectionRefOptions = {},
) {
  const onIntersectRef = useRef(onIntersect);
  useEffect(() => {
    onIntersectRef.current = onIntersect;
  });

  return useCallback(
    (node: T | null) => {
      if (!node || !enabled || typeof IntersectionObserver === 'undefined')
        return undefined;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) onIntersectRef.current();
        },
        { rootMargin },
      );
      observer.observe(node);
      return () => observer.disconnect();
    },
    [enabled, rootMargin],
  );
}
